/**
 * useAutoResolveLocation
 *
 * Fires once on mount. If the location has a name/address but no coordinates
 * (lat === 0 && lng === 0), attempts to resolve coordinates via:
 *   1. Google Geocoding: name + full address (most precise)
 *   2. Google Geocoding: address only
 *   3. Regions API: name-only strict match (own venue database)
 *
 * Updates the form store on success. Returns resolving/error state for UI feedback.
 */

import { useState, useEffect } from 'react';
import { useApi } from 'react-ui';
import { apiFetch } from 'ts-core';
import { searchGoogleGeocoding } from '../services/googleGeocoding';
import { useFormStore } from '../stores/formStore';
import type { EventFormData } from '../types';

interface Venue {
  id: string;
  name: string;
  street: string | null;
  zip_code: string | null;
  address_locality: string | null;
  region: string | null;
  country: string | null;
  full_address: string | null;
  coordinates: { lat: number; lon: number } | null;
}

interface UseAutoResolveLocationResult {
  isResolving: boolean;
  resolveError: boolean;
}

export function useAutoResolveLocation(): UseAutoResolveLocationResult {
  const [isResolving, setIsResolving] = useState(false);
  const [resolveError, setResolveError] = useState(false);

  const { apiEndpoints, apiKey } = useApi();
  const updateField = useFormStore(s => s.updateField);

  useEffect(() => {
    // Snapshot current location at mount time to avoid stale closure issues
    const location = useFormStore.getState().formData.location;

    const { name, address, coordinates } = location;
    const hasCoords = coordinates.lat !== 0 && coordinates.lng !== 0;
    const hasInput = name.trim() || address.trim();

    if (hasCoords || !hasInput) return;

    let cancelled = false;

    const resolve = async () => {
      setIsResolving(true);
      setResolveError(false);

      try {
        // Priority 1: full query — name + full address
        const fullQuery = [name.trim(), address.trim()].filter(Boolean).join(', ');
        if (fullQuery) {
          const google = await searchGoogleGeocoding(fullQuery);
          if (!cancelled && google && applyGoogleResult(google, name, updateField)) return;
        }

        // Priority 2: address only (no venue name noise)
        if (address.trim() && address.trim() !== fullQuery) {
          const google = await searchGoogleGeocoding(address.trim());
          if (!cancelled && google && applyGoogleResult(google, name, updateField)) return;
        }

        // Priority 3: name only — Regions API with strict name match
        if (name.trim() && apiEndpoints?.regions && apiKey) {
          try {
            const res = await apiFetch(
              `${apiEndpoints.regions}?name=${encodeURIComponent(name.trim())}`,
              { method: 'GET', apiKey }
            );
            const data = await res.json();
            const venues: Venue[] = data.venues ?? [];
            const normalizedQuery = name.trim().toLowerCase();
            const match = venues.find(
              v =>
                v.coordinates !== null &&
                (v.name.toLowerCase().includes(normalizedQuery) ||
                  normalizedQuery.includes(v.name.toLowerCase()))
            );
            if (!cancelled && match?.coordinates) {
              updateField('location', {
                name: match.name,
                address: match.full_address ?? address,
                coordinates: { lat: match.coordinates.lat, lng: match.coordinates.lon },
                addressComponents: {
                  street: match.street ?? '',
                  zip: match.zip_code ?? '',
                  locality: match.address_locality ?? '',
                  country: match.country ?? '',
                  region: match.region ?? '',
                },
              });
              return;
            }
          } catch {
            // silent
          }
        }

        // All steps exhausted
        if (!cancelled) setResolveError(true);
      } catch {
        if (!cancelled) setResolveError(true);
      } finally {
        if (!cancelled) setIsResolving(false);
      }
    };

    resolve();

    return () => {
      cancelled = true;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { isResolving, resolveError };
}


function applyGoogleResult(
  result: import('../services/googleGeocoding').GoogleGeocodingResult,
  preferredName: string,
  updateField: (field: 'location', value: EventFormData['location']) => void
): boolean {
  if (!result.lat || !result.lng) return false;

  updateField('location', {
    name: preferredName || (result.formattedAddress.split(',')[0] ?? ''),
    address: result.formattedAddress,
    coordinates: { lat: result.lat, lng: result.lng },
    addressComponents: {
      street: result.street,
      zip: result.zip,
      locality: result.locality,
      country: result.countryCode,
      region: result.region,
    },
  });
  return true;
}
